import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { VoiceConversationOptions } from "./helpers";
import { useVoiceConversation } from "./use-voice-conversation";

export type VoiceConversationContextValue = {
  isActive: boolean;
  isListening: boolean;
  isSupported: boolean;
  toggle: () => void;
  setActive: (isActive: boolean) => void;
};

const voiceConversationContext = createContext<
  VoiceConversationContextValue | undefined
>(undefined);

export type VoiceConversationProviderProps = {
  children: ReactNode;
  options: VoiceConversationOptions;
};

export const VoiceConversationProvider = ({
  children,
  options,
}: VoiceConversationProviderProps) => {
  const [isActive, setIsActive] = useState(false);
  const hasStartedRef = useRef(false);
  const { isListening, startListening, stopListening, isSupported } =
    useVoiceConversation(options);

  useEffect(() => {
    if (isActive && !hasStartedRef.current) {
      hasStartedRef.current = true;
      startListening().catch(console.error);
      return;
    }

    if (!isActive && hasStartedRef.current) {
      hasStartedRef.current = false;
      stopListening();
    }
  }, [isActive, startListening, stopListening]);

  const toggle = useCallback(() => {
    setIsActive((current) => !current);
  }, []);

  const value = useMemo<VoiceConversationContextValue>(
    () => ({
      isActive,
      isListening,
      isSupported,
      toggle,
      setActive: setIsActive,
    }),
    [isActive, isListening, isSupported, toggle],
  );

  return (
    <voiceConversationContext.Provider value={value}>
      {children}
    </voiceConversationContext.Provider>
  );
};

export const useVoiceConversationContext =
  (): VoiceConversationContextValue => {
    const context = useContext(voiceConversationContext);
    if (!context) {
      throw new Error(
        "Voice conversation context is not available. Use VoiceConversationProvider.",
      );
    }
    return context;
  };
